'use client';

import * as Sentry from '@sentry/nextjs';
import { useField, useFormikContext } from 'formik';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';

import { SelectOption } from '@/app/components/Select/Select';
import { Country } from '@/app/enums';
import { Key } from '@react-types/shared';

import {
  SmartFormAddressSuggestion,
  SmartFormCountry,
  SmartFormFieldTypes,
  getAddressSuggestions,
} from '@/app/utils/smartform';

import FormAutocomplete from '@/app/components/Autocomplete/FormAutocomplete';
import FormSelect from '@/app/components/Select/FormSelect';

interface AddressFormPartProps {
  countrySelectOptions: SelectOption<Country>[];
}

const AddressFormPart: FC<AddressFormPartProps> = ({ countrySelectOptions }) => {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<SmartFormAddressSuggestion[] | null>(null);
  const [fieldBeingEdited, setFieldBeingEdited] = useState<SmartFormFieldTypes | null>(null);
  const form = useFormikContext();
  const [streetAndHouseNumberField, _streetAndHouseNumberMeta, streetAndHouseNumberHelpers] =
    useField('streetAndHouseNumber');

  const [cityField, _cityMeta, cityHelpers] = useField('city');
  const [zipCodeField, _zipCodeMeta, zipCodeHelpers] = useField('zipCode');
  const [countryField] = useField('country');

  const notEmptyFields = useMemo(() => {
    const fields = [
      streetAndHouseNumberField.value ? SmartFormFieldTypes.StreetAndNumber : undefined,
      cityField.value ? SmartFormFieldTypes.City : undefined,
      zipCodeField.value ? SmartFormFieldTypes.Zip : undefined,
    ];

    return fields.filter(Boolean) as SmartFormFieldTypes[];
  }, [streetAndHouseNumberField.value, cityField.value, zipCodeField.value]);

  const suggestionsFieldType = useMemo(() => {
    if (!suggestions?.length) {
      return undefined;
    }

    return suggestions[0].fieldType;
  }, [suggestions]);

  const optionLabelFieldType = useMemo(() => {
    switch (suggestionsFieldType) {
      case SmartFormFieldTypes.Zip:
        return SmartFormFieldTypes.Zip;

      case SmartFormFieldTypes.City:
        return SmartFormFieldTypes.City;

      case SmartFormFieldTypes.StreetAndNumber:
      default:
        return 'WHOLE_ADDRESS';
    }
  }, [suggestionsFieldType]);

  const autocompleteOptions = useMemo(() => {
    if (!suggestions?.length || !suggestionsFieldType) {
      return undefined;
    }

    return suggestions.map((suggestion, index) => ({
      id: index,
      name: suggestion.values[optionLabelFieldType],
    }));
  }, [suggestions, suggestionsFieldType, optionLabelFieldType]);

  const handleFetchSuggestions = useCallback(async () => {
    if (
      countryField.value &&
      !Object.values(SmartFormCountry)
        .map((country) => country.toUpperCase())
        .includes(countryField.value?.toUpperCase())
    ) {
      return;
    }

    try {
      const response = await getAddressSuggestions({
        id: 1,
        fieldType: fieldBeingEdited ?? notEmptyFields[0],
        values: {
          STREET_AND_NUMBER: streetAndHouseNumberField.value,
          CITY: cityField.value,
          ZIP: zipCodeField.value,
        },
        country: countryField.value,
        limit: 10,
      });

      setSuggestions(response?.suggestions ?? null);
    } catch (error: unknown) {
      Sentry.captureException(error);
      return;
    }
  }, [
    streetAndHouseNumberField.value,
    cityField.value,
    zipCodeField.value,
    countryField.value,
    notEmptyFields,
    setSuggestions,
    fieldBeingEdited,
  ]);

  const handleChangeValues = useCallback(
    (suggestion: SmartFormAddressSuggestion) => {
      if (suggestion.values.STREET_AND_NUMBER || suggestion.values.STREET) {
        streetAndHouseNumberHelpers.setValue(suggestion.values.STREET_AND_NUMBER ?? suggestion.values.STREET);
      }

      if (suggestion.values.CITY_EXTENDED || suggestion.values.CITY) {
        cityHelpers.setValue(suggestion.values.CITY_EXTENDED ?? suggestion.values.CITY);
      }

      if (suggestion.values.ZIP) {
        zipCodeHelpers.setValue(suggestion.values.ZIP);
      }

      // Errors are still displayed after setting the value, so we need to revalidate the form
      // But we need to wait a bit, because the form is not yet updated
      setTimeout(form.validateForm, 100);
    },
    [streetAndHouseNumberHelpers, cityHelpers, zipCodeHelpers],
  );

  const handleSelectOption = useCallback(
    (optionKey: Key | null) => {
      if (!suggestions?.length || optionKey === null) {
        return;
      }

      const selectedOption = suggestions.find((_, index) => String(index) === String(optionKey));

      if (!selectedOption) {
        return;
      }

      handleChangeValues(selectedOption);
    },
    [suggestions],
  );

  const handleStreetAndHouseNumberChange = useCallback(() => {
    setFieldBeingEdited(SmartFormFieldTypes.StreetAndNumber);
  }, [setFieldBeingEdited]);

  const handleCityChange = useCallback(() => {
    setFieldBeingEdited(SmartFormFieldTypes.City);
  }, [setFieldBeingEdited]);

  const handleZipChange = useCallback(() => {
    setFieldBeingEdited(SmartFormFieldTypes.Zip);
  }, [setFieldBeingEdited]);

  const handleFieldBlur = useCallback(() => {
    setFieldBeingEdited(null);
  }, [setFieldBeingEdited]);

  useDebounce(
    () => {
      handleFetchSuggestions();
    },
    200,
    [streetAndHouseNumberField.value, cityField.value, zipCodeField.value, countryField.value, handleFetchSuggestions],
  );

  return (
    <>
      <FormAutocomplete
        name="streetAndHouseNumber"
        label={t('components.DonationForm.streetAndHouseNumber')}
        options={suggestionsFieldType === SmartFormFieldTypes.StreetAndNumber ? autocompleteOptions : []}
        onSelectOption={handleSelectOption}
        onChange={handleStreetAndHouseNumberChange}
        onBlur={handleFieldBlur}
        id="street-input"
      />
      <FormAutocomplete
        name="city"
        label={t('components.DonationForm.city')}
        options={suggestionsFieldType === SmartFormFieldTypes.City ? autocompleteOptions : []}
        onSelectOption={handleSelectOption}
        onChange={handleCityChange}
        onBlur={handleFieldBlur}
        id="city-input"
      />
      <FormAutocomplete
        name="zipCode"
        label={t('components.DonationForm.zip')}
        options={suggestionsFieldType === SmartFormFieldTypes.Zip ? autocompleteOptions : []}
        onSelectOption={handleSelectOption}
        onChange={handleZipChange}
        onBlur={handleFieldBlur}
        id="zipCode-input"
      />
      <FormSelect name="country" label={t('components.DonationForm.country')} options={countrySelectOptions} />
    </>
  );
};

export default AddressFormPart;
